import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import translationsData from '../data/translations';
import type { Language, Localised } from '../types';

const translations = translationsData as Record<Language, Record<string, string>>;

interface LanguageValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, fallback?: string) => string;
  local: <T>(value: Localised<T> | undefined, fallback?: T) => T | undefined;
}

const LanguageContext = createContext<LanguageValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('jaime-language');
    return stored === 'zh' || stored === 'fr' ? stored : 'en';
  });

  const setLanguage = (next: Language) => setLanguageState(next);

  useEffect(() => {
    localStorage.setItem('jaime-language', language);
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-GB';
    document.body.dataset.language = language;
  }, [language]);

  const value = useMemo<LanguageValue>(() => ({
    language,
    setLanguage,
    t: (key, fallback = key) => translations[language]?.[key] ?? translations.en?.[key] ?? fallback,
    local: <T,>(value: Localised<T> | undefined, fallback?: T) => value?.[language] ?? value?.en ?? fallback,
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) throw new Error('useLanguage must be used inside LanguageProvider');
  return value;
}
